import { FormEvent, useState } from 'react';
import { submitBetaAccessRequest } from '../services/betaAccess';

interface BetaAccessFormProps {
  initialEmail?: string;
  onSuccess?: () => void;
}

export default function BetaAccessForm({ initialEmail = '', onSuccess }: BetaAccessFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [profession, setProfession] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [location, setLocation] = useState('');
  const [useType, setUseType] = useState<'Personal' | 'Company'>('Personal');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await submitBetaAccessRequest({
        email,
        profession,
        intendedUse,
        location,
        useType,
        companyName: useType === 'Company' ? companyName : undefined,
      });

      if (response.success) {
        setSuccess(response.message);
        if (onSuccess) onSuccess();
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card" style={{ padding: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Beta access</div>
        <h2 className="display tight" style={{ fontSize: 28, margin: '0 0 8px' }}>Request access.</h2>
        <p className="muted-2" style={{ fontSize: 13.5, margin: 0, lineHeight: 1.55 }}>
          Entire is in private beta. Tell us a little about you and we'll be in touch.
        </p>
      </div>

      {success && (
        <div
          style={{
            padding: '10px 14px',
            border: '1px solid rgba(94,230,184,0.25)',
            color: 'var(--ok)',
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {success}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '10px 14px',
            border: '1px solid var(--danger-line)',
            color: 'var(--danger)',
            background: 'var(--danger-wash)',
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="field">
          <label htmlFor="profession">Profession</label>
          <input
            id="profession"
            type="text"
            required
            className="input"
            placeholder="Game developer, designer, educator…"
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="field">
          <label htmlFor="intendedUse">How do you intend to use Entire?</label>
          <textarea
            id="intendedUse"
            required
            rows={4}
            className="textarea"
            placeholder="Tell us about your project or use case…"
            value={intendedUse}
            onChange={(e) => setIntendedUse(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="field">
          <label htmlFor="location">Location</label>
          <input
            id="location"
            type="text"
            required
            className="input"
            placeholder="City, Country"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="field">
          <label>Use type</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {(['Personal', 'Company'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setUseType(t)}
                className={'opt' + (useType === t ? ' selected' : '')}
                style={{ flex: 1, justifyContent: 'center', padding: '10px 12px' }}
                disabled={isLoading}
              >
                <span className="opt-title">{t}</span>
              </button>
            ))}
          </div>
        </div>

        {useType === 'Company' && (
          <div className="field">
            <label htmlFor="companyName">Company name</label>
            <input
              id="companyName"
              type="text"
              required
              className="input"
              placeholder="Your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={isLoading}
            />
          </div>
        )}

        <button
          className="btn primary lg"
          style={{ width: '100%', justifyContent: 'center', marginTop: 6 }}
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Submitting…' : 'Submit request'}
        </button>
      </form>
    </div>
  );
}
